/**
 * Employee Management Routes Configuration
 *
 * Routes for the Employee Management module under MAUD ERP
 * Base path: /app/maud-erp/employee-management
 */

import { MODULE_HEADER_BG } from '@/theme/moduleHeader';
import { lazy } from 'react';
import { Outlet, type RouteObject } from 'react-router-dom';
import { reportsRoutes } from '@/modules/core/reports';
import { ModuleBannerProvider, type ModuleBanner } from '@/modules/core/layout/moduleBanner';
import { MODULE_HEADER_GHOST_BTN } from '@/theme/moduleHeader';
import { UdaScopeButton, useEmpUdaScope } from '../masters/components/udaScope';

// Import Employee Management component from local module
const EmployeeManagement = lazy(() => import('../index'));
const UsersList = lazy(() => import('../users-list'));
const PassThroughLayout = () => <Outlet />;

// Setup and Reports draw the slim module masthead in Employee Management's emerald.
// Setup carries the module's UDA button (the same scope the masters use); the report
// screens draw the picker the report data obeys themselves.
const EMP_BANNER_BG = MODULE_HEADER_BG;
function EmpSetupUdaButton() {
  const scope = useEmpUdaScope();
  return <UdaScopeButton scope={scope} className={MODULE_HEADER_GHOST_BTN} />;
}
const EMP_SETUP_BANNER: ModuleBanner = { background: EMP_BANNER_BG, title: 'Setup', udaSlot: <EmpSetupUdaButton /> };
const EMP_REPORTS_BANNER: ModuleBanner = { background: EMP_BANNER_BG, title: 'Reports' };

// Shared Setup page
const SetupPage = lazy(() => import('@/modules/core/setup'));

// Master components for Setup sub-pages
const DepartmentManagement = lazy(() => import('../masters/departments'));
const DesignationManagement = lazy(() => import('../masters/designations'));
const EmployeeTypeManagement = lazy(() => import('../masters/employee-types'));
const PositionManagement = lazy(() => import('../masters/positions'));
const GradeManagement = lazy(() => import('../masters/grades'));
const FunctionaryManagement = lazy(() => import('../masters/functionaries'));
const RoleManagement = lazy(() => import('../masters/roles'));
const HierarchyTypeManagement = lazy(() => import('../masters/hierarchy-types'));
const BoundaryTypeManagement = lazy(() => import('../masters/boundary-types'));
const BoundaryManagement = lazy(() => import('../masters/boundaries'));
const FundManagement = lazy(() => import('../masters/funds'));
const FunctionManagement = lazy(() => import('../masters/functions'));
const BankManagement = lazy(() => import('../masters/banks'));
const PositionAccessView = lazy(() => import('../masters/position-access-view'));
const SvmEmployees = lazy(() => import('../masters/svm-employees'));
const SvmBoundaries = lazy(() => import('../masters/svm-boundaries'));
const UlbEmployeesDashboard = lazy(() => import('../masters/ulb-employees-dashboard'));
const DscRegistration = lazy(() => import('../masters/dsc-registration'));

// Child routes for Employee Management
export const employeeManagementRoutes: RouteObject[] = [
  {
    index: true,
    element: <UlbEmployeesDashboard />
  },
  {
    path: 'dashboard',
    element: <UlbEmployeesDashboard />
  },
  {
    path: 'list',
    element: <EmployeeManagement />
  },
  {
    path: 'users-list',
    element: <UsersList />
  },
  {
    path: 'setup',
    element: <ModuleBannerProvider value={EMP_SETUP_BANNER}><SetupPage /></ModuleBannerProvider>
  },
  // Setup sub-routes - reusing admin master components
  {
    path: 'setup/department-types',
    element: <DepartmentManagement />
  },
  {
    path: 'setup/designation-types',
    element: <DesignationManagement />
  },
  {
    path: 'setup/employee-types',
    element: <EmployeeTypeManagement />
  },
  {
    path: 'setup/positions',
    element: <PositionManagement />
  },
  {
    path: 'setup/grades',
    element: <GradeManagement />
  },
  {
    path: 'setup/functionaries',
    element: <FunctionaryManagement />
  },
  {
    path: 'setup/roles',
    element: <RoleManagement />
  },
  {
    path: 'setup/hierarchy-types',
    element: <HierarchyTypeManagement />
  },
  {
    path: 'setup/boundary-types',
    element: <BoundaryTypeManagement />
  },
  {
    path: 'setup/boundaries',
    element: <BoundaryManagement />
  },
  {
    path: 'setup/funds',
    element: <FundManagement />
  },
  {
    path: 'setup/functions',
    element: <FunctionManagement />
  },
  {
    path: 'setup/banks',
    element: <BankManagement />
  },
  {
    path: 'setup/position-access-view',
    element: <PositionAccessView />
  },
  {
    path: 'svm-employees',
    element: <SvmEmployees />
  },
  {
    path: 'dsc-registration',
    element: <DscRegistration />
  },
  {
    path: 'svm-boundaries',
    element: <SvmBoundaries />
  },
  {
    path: 'reports',
    element: <ModuleBannerProvider value={EMP_REPORTS_BANNER}><PassThroughLayout /></ModuleBannerProvider>,
    children: reportsRoutes
  },
];

export default employeeManagementRoutes;
